/**
 * Wire types shared between the shell tenant activity log and the
 * admin cross-tenant activity page. Match the server responses in
 * services/api/src/routes/audit.ts and .../admin/audit.ts.
 */

export interface AuditRow {
  id: string;
  action: string;
  resource_type: string | null;
  resource_id: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  user_name: string | null;
  user_email: string | null;
  /** Present only in admin responses. */
  org_id?: string;
  /** Present only in admin responses. */
  org_name?: string | null;
  ip_address?: string | null;
  user_agent?: string | null;
}

export interface AuditPage {
  logs: AuditRow[];
  nextCursor: string | null;
}

export interface AuditFacetsBase {
  actions: string[];
  resourceTypes: string[];
  users: Array<{ id: string; name: string | null; email: string }>;
}

export interface AuditFacets extends AuditFacetsBase {
  /** Present only in admin facets. */
  orgs?: Array<{ id: string; name: string }>;
}

/**
 * Filter state as tracked by the UI. Server accepts the same fields as
 * snake_case query params — the useAuditQuery hook does the conversion.
 * Empty string means "no filter".
 */
export interface AuditFilterState {
  q: string;
  action: string;
  resourceType: string;
  userId: string;
  from: string;
  to: string;
  /** Admin only. */
  orgId: string;
}

export const EMPTY_FILTERS: AuditFilterState = {
  q: "",
  action: "",
  resourceType: "",
  userId: "",
  from: "",
  to: "",
  orgId: "",
};

export function hasActiveFilters(f: AuditFilterState): boolean {
  return (
    f.q !== "" ||
    f.action !== "" ||
    f.resourceType !== "" ||
    f.userId !== "" ||
    f.from !== "" ||
    f.to !== "" ||
    f.orgId !== ""
  );
}

/**
 * Convert a UI filter-state object into the `?foo=bar&…` query string the
 * server expects. Empty values are omitted.
 */
export function filtersToQueryString(
  f: AuditFilterState,
  extras: { cursor?: string | null; limit?: number } = {}
): string {
  const params = new URLSearchParams();
  if (f.q) params.set("q", f.q);
  if (f.action) params.set("action", f.action);
  if (f.resourceType) params.set("resource_type", f.resourceType);
  if (f.userId) params.set("user_id", f.userId);
  if (f.from) params.set("from", f.from);
  if (f.to) params.set("to", f.to);
  if (f.orgId) params.set("org_id", f.orgId);
  if (extras.cursor) params.set("cursor", extras.cursor);
  if (extras.limit) params.set("limit", String(extras.limit));
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}
